import { createContext, useContext } from 'react'
import { configManager } from '../../Managers/Config'
import { EventManager } from '../../Managers/Event/EventManager'
import { crudResourceRegistry } from '../../Services/CrudResourceRegistry'
import { GetResourceTask } from '../../Tasks/Resources/GetResourceTask'
import { GetRestUrlTask } from '../../Tasks/Resources/GetRestUrlTask'
import { DeepCloneTask } from '../../Tasks/Utils/DeepCloneTask'
import { MergeConfigAndPropsTask } from '../../Tasks/Utils/MergeConfigAndPropsTask'
import Error403 from '../Error/Error403'
import { CrudResourceStore } from './Stores/CrudResourceStore'
import { CreateActionHandlerTask } from './Tasks/CreateActionHandlerTask'
import { DeleteActionHandlerTask } from './Tasks/DeleteActionHandlerTask'
import { EditActionHandlerTask } from './Tasks/EditActionHandlerTask'
import { EditButtonHandlerTask } from './Tasks/EditButtonHandlerTask'
import { ReloadActionHandlerTask } from './Tasks/ReloadActionHandlerTask'
import { ReloadDataActionHandlerTask } from './Tasks/ReloadDataActionHandlerTask'
import { SaveButtonHandlerTask } from './Tasks/SaveButtonHandlerTask'
import { ViewActionHandlerTask } from './Tasks/ViewActionHandlerTask'
import { ViewButtonHandlerTask } from './Tasks/ViewButtonHandlerTask'
import { CrudResourceTemplate } from './Templates/CrudResourceTemplate'
import { userManager } from '@/Core/Managers'

export const CrudResourceContext = createContext()

/**
 * Створює або отримує контекст CRUD ресурсу
 *
 * @param {Object} params
 * @param {Object} params.crudContext - існуючий контекст (опціонально)
 * @param {string} params.contextType - тип контексту: 'list' або 'detail'
 * @param {string} params.mode - режим для detail контексту: 'create' | 'view' | 'edit'
 * @param {string|number} params.itemId - ID елементу для detail контексту
 * @param {boolean} params.forceRecreate - примусово створити новий контекст
 * @param {string} params.registryKey - ОБОВ'ЯЗКОВИЙ ключ для registry (якщо не передано crudContext)
 */
export const createCrudResourceContext = ({ 
    crudContext, 
    contextType = 'list', 
    mode, 
    itemId, 
    forceRecreate = false,
    registryKey,
    ...props 
}) => {
    if (!!!props.resource) throw new Error('Error: createCrudResourceContext function, empty resource parameter!')

    // Валідація: registryKey обов'язковий якщо немає готового контексту
    if (!crudContext && !registryKey) {
        throw new Error(
            '[createCrudResourceContext] Параметр "registryKey" є обов\'язковим якщо не передано готовий crudContext.\n' +
            'Приклад: registryKey="Setting.User.mainList"\n' +
            'Для автоматичної генерації використовуйте функцію generateRegistryKey з @/Core/Utils/generateRegistryKey'
        )
    }

    const crudResourceConfig = configManager.get('CrudResourceConfig', {})
    const formConfig = configManager.get('FormConfig', {})
    const listConfig = configManager.get('ListConfig', {})
    props.features ??= {}

    // Отримуємо resourceName для registry
    const resource = GetResourceTask(props.resource)
    const resourceName = resource?.key

    if (!resourceName) {
        throw new Error('Resource key is required for CrudResourceRegistry')
    }

    // Використовуємо registryKey ОБОВ'ЯЗКОВО (вже провалідовано вище)
    // Додаємо userId та userUuid для ізоляції контекстів між користувачами
    const effectiveRegistryKey = `${registryKey}.${userManager?.user?.id ?? 0}.${userManager?.user?.uuid ?? '-'}`

    // Функція для створення нового контексту
    const createNewContext = () => {
    
        props.resource = resource
        props.contextType = contextType
        props.mode = mode
        props.itemId = itemId
        props = MergeConfigAndPropsTask({
            crudResourceConfig,
            props,
        })
        
        // Form
        props.form ??= {}
        props.form = MergeConfigAndPropsTask({
            formConfig,
            resourceFormProps: props?.resource?.form ?? {},
            resourceFields: props?.resource?.fields ? { fields: props.resource.fields } : {},
            formProps: props.form,
        })

        if (!props.form.fields && props?.resource?.fields) {
            props.form.fields = props.resource.fields
        }

        if (!props.form.fields?.id) {
            props.form.fields.id ??= { label: 'Id' }
        }

        // Filter
        props.filter = MergeConfigAndPropsTask({
            filterProps: props.filter,
            resourceFilterProps: props?.resource?.filter ?? {},
        })
        props.filter.fields ??= {}
        if (Object.keys(props.filter.fields).length === 0) props.filter.fields = DeepCloneTask(props.form.fields)

        Object.keys(props.filter.fields)
            .filter(key => props.filter.fields[key]?.type === 'object')
            .forEach(key => delete props.filter.fields[key])

        Object
            .keys(props.filter.fields)
            .forEach((key) => {
                
                let field = props.filter.fields[key]

                delete field?.isEditable
                delete field?.isRequired
                delete field?.isMultiple
                delete field?.inputProps?.disabled
                delete field?.inputProps?.mode
                
                if (field.type === 'date') field.type = 'daterange'
                if (field.type === 'datetime') field.type = 'datetimerange'
                if (field.type === 'switch' || field.type === 'checkbox') {
                    field.type = 'select'
                    field.inputProps ??= {}
                    field.options = [
                        { label: 'Так', value: true },
                        { label: 'Ні', value: false },
                    ]
                    field.inputProps.allowClear = true
                }

                if (field.type === 'select' && field?.isFilterMultiple === true) {
                    field.inputProps ??= {}
                    field.inputProps.mode = 'multiple'
                }
                field.tooltip = undefined;
                if ( field.type === 'status' )
                    field.type = 'select'
                if ( field.type === 'phone' )
                    field.type = 'string'
                
                if( field.type === 'string' ) {
                    field.inputProps ??= {}
                    field.inputProps.prefix = undefined
                    field.inputProps.suffix = undefined
                    field.inputProps.placeholder = undefined
                }

                props.filter.fields[key] = { ...field }
                delete props.filter.fields[key].defaultValue
            })
        
        if (Array.isArray(props.filter.addFields))
            props.filter.addFields.map((field) => {
                props.filter.fields[field.name] = field
            })
        
        // Drawer
        props.drawer = MergeConfigAndPropsTask({
            drawerProps: props.drawer,
            resourceDrawerProps: props?.resource?.drawer ?? {},
        })

        if (!props.drawer?.width) props.drawer.width = 500

        props.drawer.eventHandlers ??= {}
        props.drawer.eventHandlers.onButtons ??= {}
        props.drawer.eventHandlers.onClose ??= {}

        if (!props.drawer.isDisabledViewButton)
            props.drawer.eventHandlers.onButtons.view = ({ buttons, crudContext }) =>
                ViewButtonHandlerTask({ buttons, crudContext })
        if (!props.drawer.isDisabledEditButton)
            props.drawer.eventHandlers.onButtons.edit = ({ buttons, crudContext }) =>
                EditButtonHandlerTask({ buttons, crudContext })
        if (!props.drawer.isDisabledSaveButton)
            props.drawer.eventHandlers.onButtons.save = ({ buttons, crudContext }) =>
                SaveButtonHandlerTask({ buttons, crudContext })

        // List
        props.list ??= {}
        props.list = MergeConfigAndPropsTask({
            listConfig,
            listProps: props?.list ?? {},
            resourceListProps: props?.resource?.list ?? {},
            other: {
                prefilter: props?.prefilter ?? props?.resource?.prefilter ?? {},
                filter: props?.filter?.initialValues ?? {},
                restUrl: GetRestUrlTask(props?.resource?.restUrl, 'getCollection', props?.list?.restUrlReplaceParams ?? {}),
                resource: props?.resource, // Додаємо resource для доступу до fields у трансформерах
            },
        })

        // HeaderPage
        props.headerPage = MergeConfigAndPropsTask({
            headerPageProps: props.headerPage,
            resourceHeaderPageProps: props?.resource?.headerPage ?? {},
        })
        // Set default access templateError403
        props.templateError403 ??= Error403
        // Set default template
        props.template ??= CrudResourceTemplate
        props.actions ??= {}
        
        // Створюємо тимчасовий eventManager для виконання onBeforeInit
        const tempEventManager = new EventManager()
        
        // Виконуємо всі onBeforeInit події з переданих пропсів
        if (props.eventHandlers?.onBeforeInit) {
            Object.entries(props.eventHandlers.onBeforeInit).forEach(([name, handler]) => {
                tempEventManager.on('onBeforeInit', name, handler)
            })
        }
        
        // Emit onBeforeInit події - передаємо props по ссылці для можливості змін
        tempEventManager.emit('onBeforeInit', { props })
        
        // Create new CrudResourceStore instance
        // Важливо: створюємо об'єкт actions до створення newContext,
        // але функції будуть використовувати newContext через замикання
        let newContext
        
        const actions = {
            create: (initialValues, openResourceCardParams) => CreateActionHandlerTask({ initialValues, crudContext: newContext, openResourceCardParams }),
            view: (item, openResourceCardParams) => ViewActionHandlerTask({ item, crudContext: newContext, openResourceCardParams }),
            edit: (item, openResourceCardParams) => EditActionHandlerTask({ item, crudContext: newContext, openResourceCardParams }),
            delete: (item) => DeleteActionHandlerTask({ item, crudContext: newContext }),
            reload: () => ReloadActionHandlerTask({ crudContext: newContext }),
            reloadData: () => ReloadDataActionHandlerTask({ crudContext: newContext }),
        }
        props.actions = { ...actions, ...props.actions }
        
        newContext = new CrudResourceStore({ ...props })

        newContext.getEventManager().emit('onAfterInit', { crudContext: newContext })

        return newContext
    }

    // Якщо передано існуючий контекст, оновлюємо його та повертаємо
    if (crudContext) {
        crudContext.mergeToInstance({ ...props })
        return crudContext
    }

    // ЗАВЖДИ використовуємо registry (useRegistry видалено)
    // Усі контексти зберігаються в registry для гарантії зв'язку список↔картка
    
    if (contextType === 'list') {
        // Для списку - єдиний контекст на ресурс (або на кастомний ключ)
        const listContext = crudResourceRegistry.getOrCreateListContext(
            effectiveRegistryKey,
            createNewContext,
            forceRecreate
        )
        listContext.contextType = 'list'
        return listContext
    } else if (contextType === 'detail') {
        // Для карточки - окремий контекст для кожної карточки
        if (!mode) {
            throw new Error('mode is required for detail context type')
        }

        const detailContext = crudResourceRegistry.getOrCreateDetailContext(
            {
                resourceName: effectiveRegistryKey,
                mode,
                itemId: mode === 'create' ? undefined : itemId,
            },
            createNewContext,
            forceRecreate
        )
        detailContext.contextType = 'detail'
        return detailContext
    }

    // Fallback - не повинно досягатися завдяки валідації
    throw new Error(`[createCrudResourceContext] Невідомий contextType: ${contextType}`)
}

export const useCrudResourceContext = () => useContext(CrudResourceContext)

export const CrudResourceContextProvider = ({ value, children }) => (
    <CrudResourceContext.Provider value={value}>{children}</CrudResourceContext.Provider>
)
